/**
 * viewportRenderPass — multi-pass WebGL render orchestration for paper-space
 * viewports (MC3).
 *
 * Each paper-space VIEWPORT entity defines a rectangular window into model
 * space.  This module drives the per-viewport render pass sequence:
 *
 *   1. Convert VP paper-space coordinates → screen pixels via the active
 *      paper-space camera to obtain the scissor rect.
 *   2. Save relevant WebGL / THREE.js state (scissor, autoClear).
 *   3. Enable SCISSOR_TEST and call gl.scissor(x, y, w, h).
 *   4. Build a temporary OrthographicCamera for the viewport's model-space
 *      view (viewCenter / viewHeight / viewTwistAngle / aspect).
 *   5. Call gl.render(modelScene, vpCamera).
 *   6. Restore the saved state via popWebGLState().
 *
 * # WebGL state discipline
 *
 * State changes are wrapped in pushWebGLState / popWebGLState helpers so
 * leaks cannot corrupt the global render state.  The `finally` block in
 * renderViewportPass guarantees restoration even on unexpected throws.
 *
 * # Budget cap
 *
 * When `visibleIds.size > VIEWPORT_ENTITY_BUDGET` the pass falls back to
 * bbox-LOD-only mode: only the viewport border is drawn (by
 * ViewportFrameLayer) and the expensive entity render is skipped.  This
 * prevents GPU overload on pathological drawings with hundreds of thousands
 * of entities visible through a single viewport.
 *
 * # Rotation (twist)
 *
 * `viewTwistAngle` rotates the view around the model-space Z axis.  The VP
 * camera matrix accounts for this rotation so the scissor rect (which is
 * always axis-aligned) yields the correct clip even for tilted views.  The
 * scissor is computed from the VP's paper-space AABB (not the rotated
 * polygon).
 *
 * # Non-rectangular clip (XCLIP / VPCLIP / SPATIAL_FILTER)
 *
 * When the viewport carries a resolved `clipPath` (a ≥3-vertex WCS polygon
 * produced by `drawing-model/viewportClipFinalize.resolveViewportClip` from the
 * ext-dict → ACAD_FILTER → SPATIAL → SPATIAL_FILTER chain), the rectangular
 * scissor alone is NOT sufficient — geometry inside the VP rect but outside the
 * clip polygon (e.g. past a slanted pentagon edge) must be cut.  This pass wires
 * the stencil implementation in `viewportClipping.ts`: it marks the clip polygon
 * into the stencil buffer (through the SAME per-VP model camera) and EQUAL-tests
 * every model material against it for the model render, so only fragments inside
 * the polygon survive.  Viewports with no `clipPath` render unclipped (the
 * scissor rect is the full rectangular window — AutoCAD parity).
 *
 * # Coordinate systems
 *
 *   - Paper-space: world units matching the sheet (mm/inches), origin at
 *     `paperOrigin` (from LayoutPageSetup.origin).
 *   - Model-space: OFFSET-world — the DXF/WCS coordinate minus the locked
 *     per-session `coordinateOrigin`. Every geometry adapter applies it, so
 *     the per-VP model camera (`buildViewportModelCamera`) and the stencil
 *     clip polygon (`viewports/viewportClipping`) must both be expressed in
 *     it, or they miss the geometry by the whole origin.
 *   - Screen pixels: (0,0) at the LOWER-LEFT of the canvas in WebGL.
 *     THREE projects from world-space through the paper-space camera into
 *     NDC (−1…+1), then we map NDC to pixel coords.
 *   - `gl.scissor` and `gl.viewport` use lower-left origin (Y up).
 */

import * as THREE from "three";
import {
  viewportWcsViewCenter,
  type ViewportEntity,
} from "@dxf-ai-editor/drawing-model";

import {
  isHiddenByLayerChain,
  isLayerChainPresent,
} from "../layerChainVisibility";
import { MODEL_SPACE_BACKGROUND_RGB01 } from "./layoutForeground";
import {
  readViewportPointProxy,
  viewportPointProxyDrawsIn,
} from "./viewportPointProxyStamp";
import {
  beginViewportClipStencil,
  type ViewportClipStencilPass,
} from "../viewports/viewportClipping";
import { applyOffset } from "../coordinateOrigin";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/**
 * Maximum number of entity ids in the culling result before the pass
 * degrades to bbox-LOD-only (no per-entity geometry).  Prevents GPU
 * overload on pathological drawings.
 */
export const VIEWPORT_ENTITY_BUDGET = 100_000;

// ---------------------------------------------------------------------------
// WebGL state push / pop
// ---------------------------------------------------------------------------

/** Snapshot of WebGL state fields touched by the viewport render pass. */
export interface SavedGLState {
  scissorEnabled: boolean;
  scissorBox: [number, number, number, number];
  /** WebGL viewport rect (x, y, w, h in physical pixels). */
  viewportRect: THREE.Vector4;
  autoClear: boolean;
  autoClearColor: boolean;
  autoClearDepth: boolean;
  autoClearStencil: boolean;
  clearColor: [number, number, number, number];
}

/**
 * Capture the current scissor + autoClear + clearColor state so it can be
 * restored after the per-viewport render pass.  Must be called BEFORE any
 * mutations.
 */
export function pushWebGLState(gl: THREE.WebGLRenderer): SavedGLState {
  const ctx = gl.getContext() as WebGLRenderingContext;
  const box = ctx.getParameter(ctx.SCISSOR_BOX) as Int32Array;
  const clearColorArr = ctx.getParameter(ctx.COLOR_CLEAR_VALUE) as Float32Array;
  const viewportRect = new THREE.Vector4();
  gl.getViewport(viewportRect);
  return {
    scissorEnabled: ctx.isEnabled(ctx.SCISSOR_TEST),
    scissorBox: [box[0]!, box[1]!, box[2]!, box[3]!],
    viewportRect,
    autoClear: gl.autoClear,
    autoClearColor: gl.autoClearColor,
    autoClearDepth: gl.autoClearDepth,
    autoClearStencil: gl.autoClearStencil,
    clearColor: [clearColorArr[0]!, clearColorArr[1]!, clearColorArr[2]!, clearColorArr[3]!],
  };
}

/**
 * Restore the WebGL state snapshot captured by pushWebGLState.  Must be
 * called in a `finally` block to guarantee execution even on throws.
 */
export function popWebGLState(
  gl: THREE.WebGLRenderer,
  saved: SavedGLState,
): void {
  const ctx = gl.getContext() as WebGLRenderingContext;
  if (saved.scissorEnabled) {
    ctx.enable(ctx.SCISSOR_TEST);
  } else {
    ctx.disable(ctx.SCISSOR_TEST);
  }
  const b = saved.scissorBox;
  ctx.scissor(b[0], b[1], b[2], b[3]);
  gl.setViewport(saved.viewportRect);
  gl.autoClear = saved.autoClear;
  gl.autoClearColor = saved.autoClearColor;
  gl.autoClearDepth = saved.autoClearDepth;
  gl.autoClearStencil = saved.autoClearStencil;
  const c = saved.clearColor;
  ctx.clearColor(c[0], c[1], c[2], c[3]);
}

// ---------------------------------------------------------------------------
// Viewport camera
// ---------------------------------------------------------------------------

/**
 * Build an OrthographicCamera for the model-space view defined by a
 * VIEWPORT entity.
 *
 *   - Frustum half-extents come from `viewHeight` and the VP aspect ratio.
 *   - The WCS point at the centre of the window maps to the camera origin
 *     projected at Z=0. That point is NOT `viewCenter`: groups 12/22 are the
 *     centre in DCS, whose origin is the WCS view TARGET (17/27/37) and whose
 *     axes are turned by the view twist, so it is
 *     `target + R(viewTwistAngle)·viewCenter` — `drawing-model`'s
 *     `viewportWcsViewCenter`, the SAME one-truth reading
 *     `dxfBridge.import.extractSavedViewFromRawTableGroups` applies to the
 *     VPORT table record's identical group codes. See that helper's module doc
 *     for the derivation, the AutoCAD-2018 measurement that settles it (five
 *     non-zero-target VIEWPORTs frame 50/23/210/41/25 model entities summed vs
 *     0/0/8/0/0 raw) and the `?? 0` additive-identity trap.
 *   - `viewTwistAngle` is applied as a Z-axis rotation on the camera (via
 *     `cam.up`) so the rendered model geometry appears at the correct
 *     orientation inside the clipped region. It therefore enters TWICE — once
 *     in the POSITION through `viewportWcsViewCenter` and once in the
 *     ORIENTATION here — and that is not double-counting: `cam.up = (−sin θ,
 *     cos θ)` makes screen-up `R(θ)·ŷ` and screen-right `R(θ)·x̂`, so a screen
 *     offset `(u, v)` lands at `centre + R(θ)·(u, v)`. Composed with the
 *     position that is `target + R(θ)·(viewCenter + (u, v))` — the one
 *     rotation factored over the sum that `ezdxf`'s inverse transform gives
 *     (see `viewportWcsViewCenter`'s twist section), and the same composition
 *     `computeViewportModelBounds` and `paperToModel` make.
 *
 * # Frame: OFFSET-world, like everything else this camera renders
 *
 * `viewCenter` / `viewTarget` come off the VIEWPORT entity in RAW DXF (WCS /
 * document) coordinates, but the model meshes this camera is pointed at are
 * built with the per-session origin subtracted (`coordinateOrigin.applyOffset`,
 * applied by every geometry adapter for Float32 precision). So the camera
 * POSITION and the lookAt TARGET — the only absolute points here — are
 * translated into that same offset-world frame before they reach THREE.
 *
 * This is the contract `viewports/viewportClipping.resolveViewportClipPolygon`
 * already states from the other side: the stencil clip polygon goes through the
 * SAME `applyOffset` "because … the per-VP model camera
 * (`buildViewportModelCamera`) renders them in that same offset-world frame".
 * Until this translation existed the clip polygon was offset and the camera was
 * not, so on a survey-scale drawing (origin (1e6, 1e6)) the camera looked a
 * whole origin away from the geometry and every paper-space viewport rendered
 * empty — a layout showed white paper with a dark, empty viewport rectangle.
 *
 * Frustum half-extents (`viewHeight`, aspect), `viewDirection` and
 * `viewTwistAngle` are translation-invariant (a size, a direction and an angle),
 * so they are used raw.
 *
 * The returned camera is temporary — the caller disposes it (or lets GC
 * collect it) after the render pass.
 */
export function buildViewportModelCamera(
  viewport: ViewportEntity,
): THREE.OrthographicCamera {
  const {
    viewCenter,
    viewHeight,
    viewTwistAngle,
    viewDirection,
    viewTarget,
    width: vpWidth,
    height: vpHeight,
  } = viewport;

  const aspectRatio = vpHeight > 0 ? vpWidth / vpHeight : 1;
  const halfH = viewHeight / 2;
  const halfW = halfH * aspectRatio;

  const cam = new THREE.OrthographicCamera(
    -halfW,
    halfW,
    halfH,
    -halfH,
    -1000,
    1000,
  );

  // Build camera position from viewTarget + viewDirection × distance (#6).
  // viewDirection is a unit vector from target toward camera (DXF convention).
  // Default distance is 100 world units (preserves the 2-D top-down view when
  // viewDirection ≈ (0,0,1) which is the standard plan-view case).
  const hasDirInfo =
    viewDirection &&
    (viewDirection.x !== 0 || viewDirection.y !== 0 || viewDirection.z !== 1);
  // lookAt PIVOT for the 3-D branch. `?? viewCenter` is a FALLBACK pivot (with
  // no target there is no DCS origin to orbit, so the stored centre is the
  // best pivot available) — it is NOT an additive identity and must never be
  // reused as the summand the plan branch needs. That is what
  // `viewportWcsViewCenter`'s `?? 0` is for.
  const tx = viewTarget?.x ?? viewCenter.x;
  const ty = viewTarget?.y ?? viewCenter.y;
  const tz = viewTarget?.z ?? 0;
  const DIST = 100;

  // Groups 12/22 are DCS; the WCS point the window is centred on is
  // `target + R(twist)·viewCenter` (see the doc comment above). Offsetting the
  // raw 12/22 pair instead put the camera a whole target-vector — 5e5 units on
  // the reporting file — off the model content it was meant to frame.
  const wcsViewCenter = viewportWcsViewCenter(viewport);

  // Document frame → renderer (offset-world) frame. Both are absolute points,
  // so both translate; see the frame section in this function's doc comment.
  const targetOffset = applyOffset({ x: tx, y: ty, z: tz });
  const centerOffset = applyOffset({
    x: wcsViewCenter.x,
    y: wcsViewCenter.y,
    z: 0,
  });

  // lookAt target: for plan view we look straight down at the WCS view centre
  // (`target + R(twist)·viewCenter`); for 3-D views we look at viewTarget (the DCS
  // origin) — see the 3-D branch's own note on what that still omits.
  let lookAtX: number;
  let lookAtY: number;
  let lookAtZ: number;

  if (hasDirInfo && viewDirection) {
    // Normalise direction vector (DXF may store un-normalised).
    const len = Math.sqrt(
      viewDirection.x ** 2 + viewDirection.y ** 2 + viewDirection.z ** 2,
    );
    const dxN = len > 0 ? viewDirection.x / len : 0;
    const dyN = len > 0 ? viewDirection.y / len : 0;
    const dzN = len > 0 ? viewDirection.z / len : 1;
    cam.position.set(
      targetOffset.x + dxN * DIST,
      targetOffset.y + dyN * DIST,
      targetOffset.z + dzN * DIST,
    );
    // 3-D view: look toward viewTarget (the DCS origin / pivot point).
    //
    // KNOWN GAP, deliberately NOT changed here (@codex PR #794 round 3 P1 asked
    // only about the plan branch's dropped target; this is its mirror image).
    // `viewCenter` is not read at all on this branch, so a 3-D view whose DCS
    // centre is non-zero is rendered UNPANNED: 12/22 says which DCS point sits
    // at the middle of the window, and the middle of this frustum is the
    // target instead. Fixing it needs the DCS BASIS, not just a sum — the
    // pivot is `target + cx·X_dcs + cy·Y_dcs`, and the only self-consistent
    // choice of those axes is the camera's own right/up AFTER `lookAt` + twist
    // (that is what makes the DCS centre land on screen centre whatever basis
    // convention is picked). That change also has no counterpart in
    // `computeViewportModelBounds`, `computeViewportViewRect` or
    // `paperToViewportModel`, none of which model `viewDirection` at all, so
    // the cull rect would disagree with the camera again. Note the reachable
    // sub-case: an UN-NORMALISED plan direction (e.g. `16/26/36 = 0,0,5`)
    // fails the `z !== 1` test above and takes this branch, losing a pan a
    // normalised `(0,0,1)` would have kept.
    lookAtX = targetOffset.x;
    lookAtY = targetOffset.y;
    lookAtZ = targetOffset.z;
  } else {
    // Plan view (viewDirection=(0,0,1)): the camera sits directly above the
    // WCS view centre (`target + R(twist)·viewCenter`) and must look straight
    // down at it, not at the WCS origin and not at the bare target — the
    // target alone is the DCS ORIGIN, not the model centre, and using it here
    // would tilt the camera ~80° off vertical when viewCenter≠(0,0).
    cam.position.set(centerOffset.x, centerOffset.y, DIST);
    lookAtX = centerOffset.x;
    lookAtY = centerOffset.y;
    // Plan view looks straight DOWN: only the XY of the target matters for the
    // orientation, and `DIST` above the Z=0 plane keeps the (translation-
    // invariant) −Z view direction the twist quaternion is built against.
    lookAtZ = 0;
  }

  // Apply view twist: set cam.up BEFORE lookAt so THREE.js builds the
  // quaternion with the correct up vector (#10).
  // viewTwistAngle is stored in degrees (DXF group 51 raw) — convert to radians.
  if (viewTwistAngle !== 0) {
    const twistRad = (viewTwistAngle * Math.PI) / 180;
    const upX = -Math.sin(twistRad);
    const upY = Math.cos(twistRad);
    cam.up.set(upX, upY, 0);
  } else {
    cam.up.set(0, 1, 0);
  }

  // lookAt called AFTER cam.up is set so twist is baked into the quaternion (#10).
  cam.lookAt(lookAtX, lookAtY, lookAtZ);

  cam.updateMatrixWorld();
  cam.updateProjectionMatrix();

  // The frustum is in model-space world units; no DPR adjustment needed.

  return cam;
}

// ---------------------------------------------------------------------------
// Paper → screen projection
// ---------------------------------------------------------------------------

/**
 * Project a viewport's paper-space bounding rectangle to screen pixel
 * coordinates using the active paper-space camera.
 *
 * Returns `null` when the viewport is fully outside the camera frustum
 * (all corners are clipped) — the caller should skip the render pass.
 *
 * Output rectangle uses WebGL lower-left origin (Y up).  Values are clamped
 * to the canvas bounds so the scissor rect never exceeds the rendering area.
 */
export function projectViewportBoundsToScreen(
  viewport: ViewportEntity,
  mainCamera: THREE.OrthographicCamera,
  canvasWidth: number,
  canvasHeight: number,
  pixelRatio: number,
  paperOrigin: { x: number; y: number },
): { x: number; y: number; w: number; h: number } | null {
  const { center, width, height } = viewport;

  // Paper-space corner positions (lower-left and upper-right of the VP rect,
  // before rotation — for scissor purposes we always use the AABB).
  const pxLL = center.x - width / 2 + paperOrigin.x;
  const pyLL = center.y - height / 2 + paperOrigin.y;
  const pxUR = center.x + width / 2 + paperOrigin.x;
  const pyUR = center.y + height / 2 + paperOrigin.y;

  // Project both corners from world space to NDC using the paper camera.
  const project = (wx: number, wy: number): THREE.Vector3 => {
    const v = new THREE.Vector3(wx, wy, 0);
    v.project(mainCamera);
    return v;
  };

  const ndcLL = project(pxLL, pyLL);
  const ndcUR = project(pxUR, pyUR);

  // NDC → screen pixels.  WebGL origin is bottom-left; canvas height flips.
  const physW = canvasWidth * pixelRatio;
  const physH = canvasHeight * pixelRatio;

  const toPixelX = (ndcX: number) => ((ndcX + 1) / 2) * physW;
  const toPixelY = (ndcY: number) => ((ndcY + 1) / 2) * physH;

  const sxLL = toPixelX(ndcLL.x);
  const syLL = toPixelY(ndcLL.y);
  const sxUR = toPixelX(ndcUR.x);
  const syUR = toPixelY(ndcUR.y);

  // Clamp to canvas bounds.
  const x = Math.max(0, Math.min(sxLL, sxUR));
  const y = Math.max(0, Math.min(syLL, syUR));
  const x2 = Math.min(physW, Math.max(sxLL, sxUR));
  const y2 = Math.min(physH, Math.max(syLL, syUR));
  const w = x2 - x;
  const h = y2 - y;

  if (w <= 0 || h <= 0) return null;
  return { x: Math.round(x), y: Math.round(y), w: Math.round(w), h: Math.round(h) };
}

// ---------------------------------------------------------------------------
// Render pass result
// ---------------------------------------------------------------------------

export type ViewportRenderPassResult =
  | "ok"
  | "budget-lod"  // too many entities — frame rendered only, no entity pass
  | "skipped";    // viewport fully outside camera frustum or zero-size

// ---------------------------------------------------------------------------
// VP visibility mask helpers (P1 #2)
// ---------------------------------------------------------------------------

/**
 * Parse the layer name from a MeshKeyId.
 * Format: `encodeURIComponent(layer)|colorHex|encodeURIComponent(linetype)`,
 * optionally followed by `|<encoded parent layer>...` for a block-child
 * bucket (`MeshKey.parentLayers`) — the first field is always the own layer.
 */
export function extractLayerFromKeyId(keyId: string): string {
  const sep = keyId.indexOf("|");
  return sep === -1 ? keyId : decodeURIComponent(keyId.slice(0, sep));
}

/**
 * Codex PR #784 P1 — a DIRECT mesh (text / hatch / wipeout fill / arrow
 * decorator) stamps `userData.entityLayer` = the layer of the renderable it
 * was built from (`directMeshLayerStamp`). For a render-only SATELLITE (an
 * INSERT wrapper's child on the cache-direct path, `scene/liftBlockInsert.ts`)
 * `userData.entityId` is the PARENT's id (so the visible-set mask keeps it
 * with the parent), while `entityLayer` stays the CHILD's own layer — AutoCAD
 * hides a nested entity when ITS layer is frozen in the viewport, independent
 * of the parent's layer, exactly what the legacy branch (children as their
 * own spatial-index entries) already did. Resolving the layer through
 * `entityId` would read the parent's layer instead.
 *
 * Round 3 P1 — the same stamp carries `userData.parentLayers`, the enclosing
 * block-reference layers: AutoCAD ALSO hides the whole reference when the
 * INSERT's own layer is VP-frozen, so the mesh is frozen when its own layer
 * OR any parent layer is (`isHiddenByLayerChain`). A mesh without the stamp
 * (legacy / foreign object) is never treated as frozen here.
 */
export function isDirectMeshOnFrozenLayer(
  userData: Readonly<Record<string, unknown>>,
  frozenSet: ReadonlySet<string> | null,
): boolean {
  if (!frozenSet) return false;
  const layer = userData["entityLayer"];
  return isHiddenByLayerChain(
    typeof layer === "string" ? layer : undefined,
    userDataParentLayers(userData),
    frozenSet,
  );
}

/** The `parentLayers` stamp of a bucket / direct mesh, if it carries one. */
function userDataParentLayers(
  userData: Readonly<Record<string, unknown>>,
): readonly string[] | undefined {
  const chain = userData["parentLayers"];
  return Array.isArray(chain) ? (chain as readonly string[]) : undefined;
}

/**
 * Walk `scene` and temporarily hide objects that must not draw for this VP:
 *
 *   - Batched line-segment buckets (`userData.keyId` present): hidden when
 *     their layer — or any enclosing block-reference layer in
 *     `userData.parentLayers` (Codex PR #784 round 3 P1) — appears in
 *     `frozenLayers`, OR when `modelLayers` is provided and the bucket's layer
 *     has NO model-space entities (paper-only layer), OR when `visibleLayers`
 *     is provided and the bucket's layer chain is not fully present in it
 *     (layer-level bucket culling — improves per-VP draw cost; a
 *     `(B, parents=[A])` bucket holds only children of INSERTs on A, so it
 *     needs a VP-visible entity on A as well as on B —
 *     {@link isLayerChainPresent}).
 *   - Standalone meshes / text objects (`userData.entityId` present): hidden
 *     when their id is NOT in `visibleIds`, OR when their OWN
 *     `userData.entityLayer` / any `userData.parentLayers` entry is frozen in
 *     this VP (a satellite child's layer AND its INSERT's —
 *     {@link isDirectMeshOnFrozenLayer}).
 *   - Attributed point-proxy clouds (`userData.viewportPointProxy`, Codex PR
 *     #784 round-12 P2): hidden unless `viewportId` is one of the viewports
 *     that REQUESTED the cloud's ids, or when the cloud's layer is frozen in
 *     this VP (`rendering/viewportPointProxyStamp.ts`).
 *
 * `viewportId` — the id of the viewport being rendered. Required for an
 * attributed point-proxy cloud to draw at all; omitting it hides every such
 * cloud (a pass that cannot name itself is not the pass that asked).
 *
 * Returns the list of objects whose `.visible` was changed from true→false so
 * the caller can call `restoreSceneVisibilityMask` in a finally block.
 * Objects already hidden before this call are not touched and not tracked.
 *
 * `visibleLayers` — optional set of layer names that have at least one entity
 * in `visibleIds`. When provided, batched buckets whose layer is absent from
 * this set are hidden, reducing geometry sent to the GPU for VP passes.
 *
 * `modelLayers` — optional set of ALL layer names that have at least one
 * model-space entity. When provided, buckets on paper-only layers (not in
 * `modelLayers`) are hidden so paper-space line segments cannot render through
 * VP windows. iter-7 P1 #2.
 */
export function applySceneVisibilityMask(
  scene: THREE.Scene,
  visibleIds: ReadonlySet<string>,
  frozenLayers: ReadonlyArray<string>,
  visibleLayers?: ReadonlySet<string>,
  modelLayers?: ReadonlySet<string>,
  viewportId?: string,
): THREE.Object3D[] {
  const frozenSet = frozenLayers.length > 0 ? new Set(frozenLayers) : null;
  const hidden: THREE.Object3D[] = [];
  scene.traverse((obj) => {
    if (!obj.visible) return; // already hidden — don't track
    const ud = obj.userData as Record<string, unknown>;
    // The `=== true` guard first: this traversal is the hottest loop in the
    // paper-space pipeline (every object, every viewport, every frame), and a
    // single property read rejects everything that is not a proxy cloud without
    // a call.
    if (ud["viewportPointProxy"] === true) {
      const pointProxy = readViewportPointProxy(ud);
      // Codex PR #784 round-12 P2 — an ATTRIBUTED point-proxy cloud
      // (`rendering/viewportPointProxyStamp.ts`). It exists because SOME
      // viewport asked for its ids at ITS own scale, so it draws in those
      // passes and nowhere else — a dot viewport A requested is `"skip"`-band
      // (or frozen, or simply absent) in viewport B, and painting it there is
      // geometry B never asked for. Every dot in the cloud shares the same
      // requester set AND the same layer by construction of the grouping, so
      // this one object-level decision is exact for all of them.
      //
      // The layer test is the SAME `entityLayer` + layer-chain predicate a
      // direct mesh gets: `viewportCullingAdapter.query` conservatively keeps
      // an id whose renderable does not resolve (the metadata-stub floor
      // withholds exactly the sub-pixel-at-main-camera ids this feed serves),
      // so a frozen-layer id CAN reach the request set and must be stopped
      // here.
      if (
        pointProxy === null ||
        !viewportPointProxyDrawsIn(pointProxy, viewportId) ||
        isDirectMeshOnFrozenLayer(ud, frozenSet)
      ) {
        obj.visible = false;
        hidden.push(obj);
      }
    } else if (typeof ud["keyId"] === "string") {
      const layer = extractLayerFromKeyId(ud["keyId"]);
      const parentLayers = userDataParentLayers(ud);
      // Hide if the layer — or an enclosing block-reference layer — is
      // frozen in this VP.
      if (frozenSet && isHiddenByLayerChain(layer, parentLayers, frozenSet)) {
        obj.visible = false;
        hidden.push(obj);
      } else if (modelLayers && !modelLayers.has(layer)) {
        // iter-7 P1 #2: paper-only layer — no model-space entities on this
        // layer; hide bucket so paper-space line segments don't render through
        // VP windows.
        obj.visible = false;
        hidden.push(obj);
      } else if (
        visibleLayers &&
        !isLayerChainPresent(layer, parentLayers, visibleLayers)
      ) {
        // Layer-level bucket culling: no entity on this layer (or on one of
        // the bucket's enclosing reference layers) is inside the VP AABB —
        // hide the whole bucket to reduce per-VP draw cost.
        obj.visible = false;
        hidden.push(obj);
      }
    } else if (ud["kind"] === "block-insert-instanced") {
      // iter-8 P1 #2: VP-pass symmetric fix for instanced INSERT meshes.
      // Hide the whole InstancedMesh if none of its per-instance insertIds
      // are in the culled visible set. Mirrors the base-pass pre-hide logic
      // (iter-7 P1 #1). Scissor rect handles per-instance spatial clipping;
      // this gate prevents a VP-invisible instanced mesh from drawing at all.
      const insertIds = ud["insertIds"] as string[] | undefined;
      const anyVisible =
        insertIds != null && insertIds.some((id) => visibleIds.has(id));
      if (!anyVisible) {
        obj.visible = false;
        hidden.push(obj);
      }
    } else if (typeof ud["entityId"] === "string") {
      // Standalone mesh (hatch, wipeout, text, dim arrow, ...): hide if not
      // in the culled visible set, or if its OWN layer is frozen in this VP
      // (a satellite child keeps the parent's `entityId` but its own layer).
      if (!visibleIds.has(ud["entityId"]) || isDirectMeshOnFrozenLayer(ud, frozenSet)) {
        obj.visible = false;
        hidden.push(obj);
      }
    }
  });
  return hidden;
}

/** Restore objects hidden by `applySceneVisibilityMask`. */
export function restoreSceneVisibilityMask(hidden: THREE.Object3D[]): void {
  for (const obj of hidden) {
    obj.visible = true;
  }
}

// ---------------------------------------------------------------------------
// renderViewportPass
// ---------------------------------------------------------------------------

export interface ViewportRenderPassArgs {
  /** THREE.WebGLRenderer — used for scissor + render call. */
  gl: THREE.WebGLRenderer;
  /** Active paper-space OrthographicCamera. */
  mainCamera: THREE.OrthographicCamera;
  /** THREE.Scene containing model-space entity meshes. */
  modelScene: THREE.Scene;
  /** Viewport entity to render. */
  viewport: ViewportEntity;
  /** Model-space entity ids visible in this VP (from ViewportCullingAdapter). */
  visibleIds: Set<string>;
  /**
   * Optional: layers that have at least one entity in `visibleIds`.  When
   * provided, batched line-segment buckets whose layer is absent from this set
   * are hidden during the VP pass (layer-level bucket culling, P1 #3).
   */
  visibleLayers?: ReadonlySet<string>;
  /**
   * Optional: ALL layer names that have at least one model-space entity.
   * When provided, buckets on paper-only layers are hidden so paper-space line
   * segments don't bleed through VP windows. iter-7 P1 #2.
   */
  modelLayers?: ReadonlySet<string>;
  /** Canvas CSS width in pixels. */
  canvasWidth: number;
  /** Canvas CSS height in pixels. */
  canvasHeight: number;
  /**
   * Paper-space sheet origin (lower-left corner) in the renderer's
   * OFFSET-world frame (`Scene3DStage`'s `paperOriginOffset`), so the
   * projected scissor rect lands on the same pixels as the offset-world
   * sheet mesh and the offset-world paper entities drawn on it.
   */
  paperOrigin: { x: number; y: number };
}

/**
 * Execute one viewport render pass:
 *
 *   1. Check budget — if visibleIds.size > VIEWPORT_ENTITY_BUDGET, return
 *      "budget-lod" without touching the GPU.
 *   2. Project VP paper-space rect to screen pixels.
 *   3. Save WebGL state.
 *   4. Enable SCISSOR_TEST + set scissor to the projected rect.
 *   5. Build model-space OrthographicCamera for this VP.
 *   6. Render modelScene through the VP camera.
 *   7. Restore saved state (always, even on error).
 */
export function renderViewportPass(
  args: ViewportRenderPassArgs,
): ViewportRenderPassResult {
  const {
    gl,
    mainCamera,
    modelScene,
    viewport,
    visibleIds,
    visibleLayers,
    modelLayers,
    canvasWidth,
    canvasHeight,
    paperOrigin,
  } = args;

  // Budget check — fall back to frame-only if too many entities.
  if (visibleIds.size > VIEWPORT_ENTITY_BUDGET) {
    return "budget-lod";
  }

  // Project VP bounds to screen.
  // projectViewportBoundsToScreen returns PHYSICAL pixels (NDC → physW/physH).
  // We need PHYSICAL pixels for the raw WebGL scissor call and LOGICAL pixels
  // (= physical / pixelRatio) for THREE.js setViewport / setScissor which
  // re-applies pixelRatio internally (#8).
  const pixelRatio = gl.getPixelRatio();
  const screenRect = projectViewportBoundsToScreen(
    viewport,
    mainCamera,
    canvasWidth,
    canvasHeight,
    pixelRatio,
    paperOrigin,
  );
  if (!screenRect) return "skipped";

  // Logical-pixel rect for THREE.js APIs (setViewport / setScissor multiply
  // by pixelRatio internally so passing physical px would double-apply DPR).
  const logicalRect = {
    x: screenRect.x / pixelRatio,
    y: screenRect.y / pixelRatio,
    w: screenRect.w / pixelRatio,
    h: screenRect.h / pixelRatio,
  };

  const saved = pushWebGLState(gl);
  // Track objects hidden for this VP pass so we can restore them in finally.
  let hiddenObjects: THREE.Object3D[] = [];
  // Non-rectangular clip stencil pass — created after the model camera is built,
  // restored (material stencil fields reverted + stencil mesh disposed) in finally.
  let clipPass: ViewportClipStencilPass | null = null;
  try {
    const ctx = gl.getContext() as WebGLRenderingContext;

    // Disable auto-clear so the VP pass composites over the paper scene.
    gl.autoClear = false;
    gl.autoClearColor = false;
    gl.autoClearDepth = false;
    gl.autoClearStencil = false;

    // Enable scissor and map the WebGL viewport to the VP pixel rect so NDC
    // maps into the VP window, not the full canvas (P1 #1).
    // ctx.scissor receives PHYSICAL pixels (raw WebGL); gl.setViewport receives
    // LOGICAL pixels (THREE.js applies pixelRatio internally) (#8).
    ctx.enable(ctx.SCISSOR_TEST);
    ctx.scissor(screenRect.x, screenRect.y, screenRect.w, screenRect.h);
    gl.setViewport(logicalRect.x, logicalRect.y, logicalRect.w, logicalRect.h);

    // Clear depth + stencil over the whole scissor rect (always): depth so VP
    // passes don't bleed into each other (#5); stencil so the non-rectangular
    // clip pass below starts from a clean buffer inside this VP's scissor rect.
    ctx.clearDepth(1.0);
    ctx.clearStencil(0);
    ctx.clear(ctx.DEPTH_BUFFER_BIT | ctx.STENCIL_BUFFER_BIT);

    // Apply VP visibility mask — hide frozen-layer buckets, paper-only layer
    // buckets (P1 #2 iter-7), out-of-AABB layer buckets (P1 #3), and
    // standalone meshes not in the culled set.
    hiddenObjects = applySceneVisibilityMask(
      modelScene,
      visibleIds,
      viewport.frozenLayers,
      visibleLayers,
      modelLayers,
      // Codex PR #784 round-12 P2 — names this pass so an attributed
      // point-proxy cloud draws in the viewport that requested its ids and is
      // hidden in every other one.
      viewport.id,
    );

    // Build and use a model-space camera for this viewport.
    const vpCamera = buildViewportModelCamera(viewport);

    // Non-rectangular clip (XCLIP / VPCLIP / SPATIAL_FILTER): when the viewport
    // carries a RESOLVABLE clipPath polygon, mark it into the stencil buffer
    // (through this SAME model camera so the polygon lands exactly on top of the
    // geometry it clips) and EQUAL-test every model material against it. The pass
    // ALSO fills the polygon interior with the dark model background under the
    // same stencil mask (Codex #326 P2) so the area outside the polygon keeps the
    // paper sheet. Returns null for a rectangular (no-clipPath) viewport — AND for
    // a DEGENERATE clip polygon (collinear / duplicate / zero-area ⇒ earcut yields
    // no triangles): in BOTH null cases the model must render UNCLIPPED over the
    // dark rectangular background (the unclipped fallback the stencil helper
    // documents for a zero-area clip). The colour clear is therefore driven by the
    // ACTUAL clip-pass result below, NOT by a `clipPath.length >= 3` guess — a
    // non-tessellatable clipPath would otherwise leave the VP transparent (neither
    // the dark interior fill nor the rect fallback ran) (Codex #326 r3 P2 #1).
    clipPass = beginViewportClipStencil(gl, modelScene, vpCamera, {
      viewportEntityId: viewport.id,
      clipPath: viewport.clipPath,
      backgroundColor: MODEL_SPACE_BACKGROUND_RGB01,
    });

    // Color background: a viewport whose clip did NOT resolve to a stencil pass
    // (rectangular OR degenerate/non-tessellatable clip) fills its whole window
    // with the dark model background (AutoCAD parity — model geometry shows on its
    // own background, the white paper does not bleed in). A viewport whose clip
    // RESOLVED must NOT color-clear the whole rect — the dark would slab over the
    // paper outside the clip polygon (Codex #326 P2); the clip pass already
    // painted the dark background INSIDE the polygon only, and the rest keeps the
    // paper. This clear runs AFTER `beginViewportClipStencil` (the resolved pass
    // wrote only the stencil mesh + the masked interior fill, neither of which the
    // rect clear would have happened over) and BEFORE the model render so the
    // model composites on top of the background either way.
    if (!clipPass) {
      // #1f1f23 — the model-space drawing-area background, DERIVED from the
      // same constant the model foreground is paired against
      // (`./layoutForeground.ts`), not a re-typed literal: this clear is the
      // background every model-space ACI-7 object inside a viewport contrasts
      // with, so the two must move together.
      ctx.clearColor(
        MODEL_SPACE_BACKGROUND_RGB01.r,
        MODEL_SPACE_BACKGROUND_RGB01.g,
        MODEL_SPACE_BACKGROUND_RGB01.b,
        1,
      );
      ctx.clear(ctx.COLOR_BUFFER_BIT);
    }

    gl.render(modelScene, vpCamera);
  } finally {
    // Revert material stencil fields + dispose the stencil mesh BEFORE restoring
    // GL state / scene visibility so the next pass (and the base scene) see
    // pristine materials.
    clipPass?.restore();
    // Restore scene visibility before restoring GL state so any synchronous
    // callbacks triggered by the render call see consistent object state.
    restoreSceneVisibilityMask(hiddenObjects);
    popWebGLState(gl, saved);
  }

  return "ok";
}
